import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaPlus, 
  FaSync, 
  FaClipboardList, 
  FaEdit, 
  FaTrash, 
  FaChevronLeft, 
  FaChevronRight,
  FaTimes,
  FaExclamationTriangle,
  FaMicrophone
} from 'react-icons/fa';
import { IoLanguage } from 'react-icons/io5';
import { FiRefreshCw } from 'react-icons/fi';
import useProverbs from '../hooks/useProverbs';
import useNotification from '../hooks/useNotification';
import { useAuth } from '../contexts/AuthContext';
import VoiceUpload from '../components/voice/VoiceUpload';
import firebaseTestService from '../services/firebaseTestService';

const ProverbsPage = ({ autoOpenAdd = false }) => {
  const { user, token } = useAuth();
  const { 
    filteredProverbs, 
    loading, 
    searchTerm, 
    setSearchTerm,
    languageFilter,
    setLanguageFilter,
    loadProverbs,
    createProverb,
    updateProverb,
    deleteProverb 
  } = useProverbs();
  const { showNotification, showDetailedNotification, showProverbNotification } = useNotification();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(autoOpenAdd);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, index: -1, proverb: null });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [voiceUpload, setVoiceUpload] = useState({ open: false, proverbId: null, language: 'chinese' });
  const [formData, setFormData] = useState({
    id: '',
    chinese_proverb: '',
    pinyin: '',
    english_translation: '',
    english_explanation: '',
    english_equivalent: '',
    french_translation: '',
    french_explanation: '',
    french_equivalent: '',
    arabic_translation: '',
    arabic_Explanation: ''
  });

  // Handle voice upload success
  const handleVoiceUploadSuccess = useCallback((result) => {
    showProverbNotification(`🎤 Voice uploaded successfully for proverb ID: ${result.proverb_id}`, 'success');
    setVoiceUpload({ open: false, proverbId: null, language: 'chinese' });
  }, [showProverbNotification]);

  // Open voice upload modal
  const openVoiceUpload = (proverbId, language = 'chinese') => {
    setVoiceUpload({ open: true, proverbId, language });
  };

  // Close voice upload modal
  const closeVoiceUpload = () => {
    setVoiceUpload({ open: false, proverbId: null, language: 'chinese' });
  };

  // Enhanced refresh function with better user feedback and Firebase testing
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      showNotification('🔄 Refreshing proverbs data...', 'info');
      
      // Test Firebase connection first
      const connectionTest = await firebaseTestService.checkFirebaseConnection();
      if (!connectionTest.connected) {
        showNotification(`⚠️ Firebase connection issue: ${connectionTest.error}`, 'warning');
      } else {
        showNotification(`✅ Firebase connected! Found ${connectionTest.proverbCount} proverbs.`, 'success');
      }
      
      // Clear current search to show all data after refresh
      setSearchTerm('');
      setLanguageFilter('');
      setCurrentPage(1);
      
      await loadProverbs();
      
      // Run Firebase integration test
      const testResult = await firebaseTestService.testFirebaseIntegration();
      if (testResult.success) {
        showDetailedNotification(
          `🔥 Firebase Integration Success!`, 
          `Found ${testResult.stats.proverbs} proverbs, ${testResult.stats.activities} activities, ${testResult.stats.voices} voices`,
          'success'
        );
      } else {
        showNotification(`⚠️ Firebase test failed: ${testResult.error}`, 'warning');
      }
      
    } catch (error) {
      console.error('Error refreshing proverbs:', error);
      showNotification('❌ Failed to refresh data. Please check your connection and try again.', 'error');
    } finally {
      setIsRefreshing(false);
    }
  }, [loadProverbs, showNotification, showDetailedNotification, setIsRefreshing, setSearchTerm, setLanguageFilter, setCurrentPage]);

  // Add keyboard shortcuts for refresh functionality
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl/Cmd + R for refresh (prevent default browser refresh)
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        handleRefresh();
      }
      // F5 for refresh
      if (event.key === 'F5') {
        event.preventDefault();
        handleRefresh();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleRefresh]);

  // Check if user is authenticated (after all hooks)
  if (!user || !token) {
    return (
      <div className="proverbs-section">
        <div className="empty-state">
          <h3>Authentication Required</h3>
          <p>Please log in to access Chinese proverbs management.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="add-btn"
          >
            🔐 Refresh Page to Login
          </button>
        </div>
      </div>
    );
  }

  const isSuperAdmin = () => user?.role_id === 1;
  const isAdmin = () => user?.role_id === 2;
  const canManageProverbs = () => {
    console.log('User:', user, 'Role ID:', user?.role_id, 'Can manage:', isSuperAdmin() || isAdmin());
    return isSuperAdmin() || isAdmin();
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Pagination
  const totalPages = Math.ceil(filteredProverbs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredProverbs.length);
  const currentItems = filteredProverbs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Modal handlers
  const openModal = (mode = 'add', proverbId = null) => {
    let proverb = null;
    let actualIndex = -1;
    
    if (mode === 'edit' && proverbId) {
      // Find the proverb by ID in the filtered proverbs
      actualIndex = filteredProverbs.findIndex(p => p.id === proverbId);
      if (actualIndex >= 0) {
        proverb = filteredProverbs[actualIndex];
      }
      
      if (!proverb) {
        console.error(`Proverb with ID ${proverbId} not found in filtered proverbs`);
        return;
      }
    }
    
    setEditingIndex(actualIndex);
    
    if (mode === 'edit' && proverb) {
      setFormData({
        id: proverb.id,
        chinese_proverb: proverb.chinese_proverb || '',
        pinyin: proverb.pinyin || '',
        english_translation: proverb.english_translation || '',
        english_explanation: proverb.english_explanation || '',
        english_equivalent: proverb.english_equivalent || '',
        french_translation: proverb.french_translation || '',
        french_explanation: proverb.french_explanation || '',
        french_equivalent: proverb.french_equivalent || '',
        arabic_translation: proverb.arabic_translation || '',
        arabic_Explanation: proverb.arabic_Explanation || ''
      });
    } else {
      setFormData({
        id: '',
        chinese_proverb: '',
        pinyin: '',
        english_translation: '',
        english_explanation: '',
        english_equivalent: '',
        french_translation: '',
        french_explanation: '',
        french_equivalent: '',
        arabic_translation: '',
        arabic_Explanation: ''
      });
    }
    
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingIndex(-1);
    setFormData({
      id: '',
      chinese_proverb: '',
      pinyin: '',
      english_translation: '',
      english_explanation: '',
      english_equivalent: '',
      french_translation: '',
      french_explanation: '',
      french_equivalent: '',
      arabic_translation: '',
      arabic_Explanation: ''
    });
    
    // Navigate back to main proverbs list and clear filters
    setCurrentPage(1);
    setSearchTerm('');
    setLanguageFilter('');
  };

  const handleSave = async () => {
    // Check permissions first
    if (!canManageProverbs()) {
      showNotification('❌ Permission denied. Admin or Super Admin role required to manage proverbs.', 'error');
      return;
    }

    // Basic validation
    if (!formData.chinese_proverb?.trim()) {
      showNotification('Chinese proverb is required', 'error');
      return;
    }
    
    if (!formData.pinyin?.trim()) {
      showNotification('Pinyin is required', 'error');
      return;
    }
    
    if (!formData.english_translation?.trim()) {
      showNotification('English translation is required', 'error');
      return;
    }

    try {
      if (editingIndex === -1) {
        await createProverb({
          ...formData,
          created_by: user?.id || 'anonymous'
        });
        showProverbNotification('✅ New proverb created successfully!', 'success');
      } else {
        const proverbToUpdate = filteredProverbs[editingIndex];
        await updateProverb(proverbToUpdate.id, formData);
        showProverbNotification('✅ Proverb updated successfully!', 'success');
      }
      
      // Refresh the proverbs list to show the changes
      await loadProverbs();
      
      closeModal();
      
      // Navigate back to main proverbs list (page 1) and clear filters
      setCurrentPage(1);
      setSearchTerm('');
      setLanguageFilter('');
      
    } catch (error) {
      console.error('Error saving proverb via backend API:', error);
      
      // Handle specific API error messages
      if (error.message.includes('Authentication required')) {
        showNotification('❌ Please log in again to continue.', 'error');
      } else if (error.message.includes('Permission denied') || error.message.includes('Admin or Super Admin role required')) {
        showNotification('❌ Admin permissions required to manage proverbs.', 'error');
      } else if (error.message.includes('Invalid proverb data')) {
        showNotification('❌ Please check all required fields are filled correctly.', 'error');
      } else if (error.message.includes('Duplicate proverb found')) {
        showDetailedNotification(
          'Duplicate Content Detected',
          error.message.replace('Duplicate proverb found! ', ''),
          'error'
        );
      } else if (error.message.includes('Failed to create proverb') || error.message.includes('Failed to update proverb')) {
        showNotification(`❌ Backend API Error: ${error.message}`, 'error');
      } else {
        showNotification(`❌ Failed to save proverb: ${error.message}`, 'error');
      }
    }
  };

  const handleDelete = async (proverbId) => {
    const proverb = filteredProverbs.find(p => p.id === proverbId);
    if (!proverb) {
      showNotification('Proverb not found', 'error');
      return;
    }
    
    setDeleteConfirmation({ 
      open: true, 
      index: -1, // No longer needed
      proverb 
    });
  };

  const confirmDelete = async () => {
    const { proverb } = deleteConfirmation;
    if (!proverb) return;
    
    try {
      await deleteProverb(proverb.id);
      showProverbNotification(`✅ Proverb "${proverb.chinese_proverb}" deleted successfully!`, 'success');
      
      // Refresh the proverbs list to show the changes
      await loadProverbs();
      
      // Close the delete confirmation dialog
      setDeleteConfirmation({ open: false, index: -1, proverb: null });
      
      // Navigate back to main proverbs list (page 1) and clear filters
      setCurrentPage(1);
      setSearchTerm('');
      setLanguageFilter('');
      
    } catch (error) {
      console.error('Error deleting proverb:', error);
      showNotification('Failed to delete proverb: ' + error.message, 'error');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ open: false, index: -1, proverb: null });
  };

  return (
    <div className="proverbs-section">
      {/* Controls */}
      <div className="controls-panel">
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search: exact ID (e.g., '8') or text in any language..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            className="language-filter"
          >
            <option value="">All Languages</option>
            <option value="chinese">Chinese</option>
            <option value="english">English</option>
            <option value="french">French</option>
            <option value="arabic">Arabic</option>
          </select>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="language-filter"
            title="Items per page"
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={15}>15 per page</option>
            <option value={20}>20 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={filteredProverbs.length || 1}>Show All ({filteredProverbs.length})</option>
          </select>
          <div className="pagination-info">
            📄 Page {currentPage} of {totalPages}
          </div>
          <div className="search-hint">
            💡 Try: "8" (exact ID) or "actions" (text search)
          </div>
        </div>
        <div className="control-buttons">
          <button 
            onClick={() => openModal('add')} 
            className="add-btn"
            disabled={loading}
            title={!canManageProverbs() ? 'Admin permissions required to add proverbs' : 'Add new proverb'}
          >
            <FaPlus /> Add New Proverb
          </button>
          <button 
            onClick={handleRefresh} 
            className="refresh-btn"
            disabled={loading || isRefreshing}
          >
            {isRefreshing ? (
              <><FiRefreshCw className="spinning-icon-small" /> Refreshing...</>
            ) : loading ? (
              'Loading...'
            ) : (
              <><FaSync /> Refresh Data</>
            )}
          </button>
        </div>
      </div>

      {/* Proverbs Table */}
      {isRefreshing ? (
        <div className="loading-spinner">
          <div className="icon-spinner">
            <FiRefreshCw className="spinning-icon" />
          </div>
          <p>{isRefreshing ? 'Refreshing Chinese Proverbs...' : 'Loading Chinese Proverbs...'}</p>
          <div className="loading-details">
            <FiRefreshCw className="spinning-icon-small" /> {isRefreshing ? 
              'Fetching the latest data from our Firebase database with all proverbs and their translations.' : 
              'Fetching wisdom from our collection of ancient Chinese proverbs with translations in multiple languages.'
            }
          </div>
        </div>
      ) : filteredProverbs.length === 0 ? (
        <div className="empty-state">
          <h3>No Chinese Proverbs Found</h3>
          <p>No proverbs match your current search criteria. Try adjusting your search or add a new proverb to get started!</p>
          <button onClick={() => openModal('add')} className="add-btn" disabled={!canManageProverbs()}>
            <FaPlus /> {canManageProverbs() ? 'Add Your First Proverb' : 'Admin Access Required'}
          </button>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="proverbs-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Chinese</th>
                  <th>Pinyin</th>
                  <th>English</th>
                  <th>Arabic</th>
                  {canManageProverbs() && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.map((proverb, index) => {
                  return (
                    <tr key={proverb.id || index}>
                      <td className="proverb-id">
                        <span className="id-badge">#{proverb.id || proverb.proverbId || (startIndex + index + 1)}</span>
                      </td>
                      <td className="proverb-row chinese">
                        {proverb.chinese_proverb}
                      </td>
                      <td className="proverb-row pinyin">
                        {proverb.pinyin}
                      </td>
                      <td className="proverb-row translation">
                        <div>{proverb.english_translation}</div>
                        {proverb.english_explanation && (
                          <div style={{fontSize: '0.8rem', color: '#a0aec0', marginTop: '0.25rem'}}>
                            {truncateText(proverb.english_explanation, 80)}
                          </div>
                        )}
                        {proverb.english_equivalent && (
                          <div style={{fontSize: '0.8rem', color: '#667eea', marginTop: '0.25rem'}}>
                            "{proverb.english_equivalent}"
                          </div>
                        )}
                      </td>
                      <td className="proverb-row arabic">
                        {proverb.arabic_translation}
                      </td>
                      {canManageProverbs() && (
                        <td className="actions-cell">
                          <div className="table-actions">
                            <button 
                              onClick={() => openVoiceUpload(proverb.id)}
                              className="voice-btn"
                              title="Upload Voice Recording"
                            >
                              <FaMicrophone />
                            </button>
                            <button 
                              onClick={() => openModal('edit', proverb.id)}
                              className="edit-btn"
                              title="Edit Proverb"
                            >
                              <FaEdit />
                            </button>
                            <button 
                              onClick={() => handleDelete(proverb.id)}
                              className="delete-btn"
                              title="Delete Proverb"
                              disabled={loading}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                <FaChevronLeft /> Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next <FaChevronRight />
              </button>
              
              <div className="page-info">
                <span className="range-indicator">
                  <FaClipboardList /> Showing #{startIndex + 1}-#{endIndex} of {filteredProverbs.length} proverbs ({itemsPerPage} per page)
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingIndex === -1 ? (
                  <><FaPlus /> Add New Proverb</>
                ) : (
                  <><FaEdit /> Edit Proverb</>
                )}
              </h3>
              <button onClick={closeModal} className="modal-close"><FaTimes /></button>
            </div>
            
            <div className="modal-body">
              <div className="form-grid">
                {/* Chinese Section */}
                <div className="form-section-title"><IoLanguage /> Chinese Original</div>
                
                <div className="form-group required">
                  <label htmlFor="chinese_proverb">Chinese Proverb</label>
                  <textarea
                    id="chinese_proverb"
                    value={formData.chinese_proverb}
                    onChange={(e) => setFormData({...formData, chinese_proverb: e.target.value})}
                    rows="2"
                    placeholder="Enter the Chinese proverb"
                    required
                  />
                </div>
                
                <div className="form-group required">
                  <label htmlFor="pinyin">Pinyin Pronunciation</label>
                  <textarea
                    id="pinyin"
                    value={formData.pinyin}
                    onChange={(e) => setFormData({...formData, pinyin: e.target.value})}
                    rows="2"
                    placeholder="Enter the pinyin pronunciation"
                    required
                  />
                </div>

                {/* English Section */}
                <div className="form-section-title">English Translation</div>
                
                <div className="form-group required">
                  <label htmlFor="english_translation">English Translation</label>
                  <textarea
                    id="english_translation"
                    value={formData.english_translation}
                    onChange={(e) => setFormData({...formData, english_translation: e.target.value})}
                    rows="3"
                    placeholder="Enter the English translation"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="english_explanation">English Explanation</label>
                  <textarea
                    id="english_explanation"
                    value={formData.english_explanation}
                    onChange={(e) => setFormData({...formData, english_explanation: e.target.value})}
                    rows="3"
                    placeholder="Explain the meaning and context"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="english_equivalent">English Equivalent</label>
                  <textarea
                    id="english_equivalent"
                    value={formData.english_equivalent}
                    onChange={(e) => setFormData({...formData, english_equivalent: e.target.value})}
                    rows="2"
                    placeholder="Similar English saying or proverb"
                  />
                </div>

                {/* French Section */}
                <div className="form-section-title">French Translation</div>

                <div className="form-group">
                  <label htmlFor="french_translation">French Translation</label>
                  <textarea
                    id="french_translation"
                    value={formData.french_translation}
                    onChange={(e) => setFormData({...formData, french_translation: e.target.value})}
                    rows="3"
                    placeholder="Traduction française"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="french_explanation">French Explanation</label>
                  <textarea
                    id="french_explanation"
                    value={formData.french_explanation}
                    onChange={(e) => setFormData({...formData, french_explanation: e.target.value})}
                    rows="3"
                    placeholder="Explication du sens et du contexte"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="french_equivalent">French Equivalent</label>
                  <textarea
                    id="french_equivalent"
                    value={formData.french_equivalent}
                    onChange={(e) => setFormData({...formData, french_equivalent: e.target.value})}
                    rows="2"
                    placeholder="Proverbe français similaire"
                  />
                </div>

                {/* Arabic Section */}
                <div className="form-section-title">Arabic Translation</div>

                <div className="form-group">
                  <label htmlFor="arabic_translation">Arabic Translation</label>
                  <textarea
                    id="arabic_translation"
                    value={formData.arabic_translation}
                    onChange={(e) => setFormData({...formData, arabic_translation: e.target.value})}
                    rows="3"
                    className="arabic-input"
                    placeholder="الترجمة العربية"
                    dir="rtl"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="arabic_explanation">Arabic Explanation</label>
                  <textarea
                    id="arabic_explanation"
                    value={formData.arabic_Explanation}
                    onChange={(e) => setFormData({...formData, arabic_Explanation: e.target.value})}
                    rows="3"
                    className="arabic-input"
                    placeholder="شرح المعنى والسياق"
                    dir="rtl"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button onClick={closeModal} className="cancel-btn" disabled={loading}>
                Cancel
              </button>
              <button onClick={handleSave} className="save-btn" disabled={loading}>
                {loading ? 'Saving...' : (editingIndex === -1 ? 'Create Proverb' : 'Update Proverb')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.open && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h3><FaTrash /> Confirm Deletion</h3>
              <button onClick={cancelDelete} className="modal-close"><FaTimes /></button>
            </div>
            
            <div className="modal-body">
              <div className="delete-confirmation-content">
                <div className="warning-icon"><FaExclamationTriangle /></div>
                <p className="warning-message">
                  Are you sure you want to delete this proverb?
                </p>
                {deleteConfirmation.proverb && (
                  <div className="proverb-preview">
                    <div className="preview-field">
                      <strong>Chinese:</strong> {deleteConfirmation.proverb.chinese_proverb}
                    </div>
                    <div className="preview-field">
                      <strong>English:</strong> {deleteConfirmation.proverb.english_translation}
                    </div>
                  </div>
                )}
                <p className="delete-warning">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="modal-footer delete-footer">
              <button onClick={cancelDelete} className="cancel-btn" disabled={loading}>
                Cancel
              </button>
              <button onClick={confirmDelete} className="delete-btn danger" disabled={loading}>
                {loading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Upload Modal */}
      {voiceUpload.open && (
        <VoiceUpload
          proverbId={voiceUpload.proverbId}
          language={voiceUpload.language}
          onUploadSuccess={handleVoiceUploadSuccess}
          onClose={closeVoiceUpload}
        />
      )}
    </div>
  );
};

export default ProverbsPage;
